import clsx from 'clsx';
import { Search, X } from 'lucide-react';
import type { InputHTMLAttributes, Ref, TextareaHTMLAttributes } from 'react';
import { useFieldControl, type FieldControlProps } from './field-context';
import styles from './controls.module.css';

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'>, FieldControlProps {
  size?: 'sm' | 'md';
  /** Monospace text (ids, tokens, regexes). */
  mono?: boolean;
  ref?: Ref<HTMLInputElement>;
}

/** Text input (any native type). Use inside `<Field>` for label/help/error. */
export function Input({ size = 'md', mono = false, invalid, className, ref, ...rest }: InputProps) {
  const a11y = useFieldControl({ id: rest.id, 'aria-describedby': rest['aria-describedby'], invalid });
  return (
    <input
      ref={ref}
      {...rest}
      {...a11y}
      className={clsx(styles.control, size === 'sm' && styles.sm, mono && styles.mono, className)}
    />
  );
}

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement>, FieldControlProps {
  mono?: boolean;
  ref?: Ref<HTMLTextAreaElement>;
}

export function Textarea({ mono = false, invalid, className, ref, ...rest }: TextareaProps) {
  const a11y = useFieldControl({ id: rest.id, 'aria-describedby': rest['aria-describedby'], invalid });
  return (
    <textarea
      ref={ref}
      {...rest}
      {...a11y}
      className={clsx(styles.control, styles.textarea, mono && styles.mono, className)}
    />
  );
}

export interface SearchInputProps extends Omit<InputProps, 'type' | 'value' | 'onChange'> {
  value: string;
  onChange: (value: string) => void;
}

/** Search box with an icon and a clear button (Escape also clears). */
export function SearchInput({
  value,
  onChange,
  className,
  placeholder = 'Search…',
  ...rest
}: SearchInputProps) {
  return (
    <div className={clsx(styles.iconWrap, className)}>
      <Search className={styles.leadingIcon} aria-hidden />
      <Input
        {...rest}
        type="search"
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape' && value) {
            e.stopPropagation();
            onChange('');
          }
          rest.onKeyDown?.(e);
        }}
      />
      {value && (
        <button type="button" className={styles.clear} aria-label="Clear search" onClick={() => onChange('')}>
          <X aria-hidden />
        </button>
      )}
    </div>
  );
}
